import React, { useState } from 'react';
import { Modal, Pressable, View } from 'react-native';
import { useAppStore } from '@restorant/core';
import type { StringKey } from '@restorant/core';
import { TEXT_SCALES, radius, scaleFont, useColors } from '../theme';
import { useT } from '../i18n';
import { Icon } from '../icons/Icon';
import { T } from './Text';

/**
 * Reading-size control. Each option previews at its own size so the choice is
 * made by looking, not by guessing what "Large" means.
 */
export function TextSizePicker({ compact = false }: { compact?: boolean }) {
  const colors = useColors();
  const t = useT();
  const textSize = useAppStore((s) => s.textSize);
  const setTextSize = useAppStore((s) => s.setTextSize);
  const [open, setOpen] = useState(false);

  const current = TEXT_SCALES.find((s) => s.id === textSize) ?? TEXT_SCALES[1];

  return (
    <>
      <Pressable
        onPress={() => setOpen(true)}
        testID="text-size"
        accessibilityLabel={t('text_size')}
        style={({ hovered }: any) => ({
          flexDirection: 'row',
          alignItems: 'center',
          gap: 7,
          paddingVertical: 8,
          paddingHorizontal: compact ? 9 : 12,
          borderRadius: radius.pill,
          borderWidth: 1,
          borderColor: hovered ? colors.gold : colors.line,
          backgroundColor: colors.surface,
        })}
      >
        <Icon name="textSize" size={15} color={colors.textDim} />
        {compact ? null : (
          <T size={12.5} weight={700} color={colors.textDim}>
            {t(current.labelKey as StringKey)}
          </T>
        )}
      </Pressable>

      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <Pressable
          onPress={() => setOpen(false)}
          style={{
            flex: 1,
            backgroundColor: colors.scrim,
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
        >
          <Pressable
            onPress={() => {}}
            style={{
              width: '100%',
              maxWidth: 360,
              backgroundColor: colors.surface,
              borderWidth: 1,
              borderColor: colors.line,
              borderRadius: radius.lg,
              padding: 18,
              gap: 8,
            }}
          >
            <T size={15} weight={600} serif style={{ marginBottom: 4 }}>
              {t('text_size')}
            </T>

            {TEXT_SCALES.map((option) => {
              const active = option.id === textSize;
              return (
                <Pressable
                  key={option.id}
                  onPress={() => {
                    setTextSize(option.id);
                    setOpen(false);
                  }}
                  testID={`text-size-${option.id}`}
                  style={({ hovered }: any) => ({
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    paddingVertical: 13,
                    paddingHorizontal: 14,
                    borderRadius: radius.md,
                    borderWidth: 1,
                    borderColor: active ? colors.gold : colors.line,
                    backgroundColor: active || hovered ? colors.surface2 : 'transparent',
                  })}
                >
                  <View style={{ flexShrink: 1, gap: 3 }}>
                    <T size={13.5} weight={active ? 700 : 600} color={active ? colors.gold : colors.text}>
                      {t(option.labelKey as StringKey)}
                    </T>
                    {/* Preview renders at the option's own size, bypassing the
                        current scale so all four can be compared at a glance. */}
                    <T
                      size={13}
                      color={colors.textFaint}
                      style={{ fontSize: scaleFont(13, option.value) }}
                    >
                      {t('size_preview')}
                    </T>
                  </View>
                  {active ? <Icon name="check" size={16} color={colors.gold} /> : null}
                </Pressable>
              );
            })}
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}
